import { getImageUrl } from './imageutil';
import placeholder from '~/assets/image/default.png';

export type ProductImageSize = 200 | 400 | 800;
const namePattern = /^[\p{L}\p{N}][\p{L}\p{N} _+().-]{0,179}\.webp$/iu;

/** Only the explicit v1 namespace participates. Never rewrite arbitrary legacy '/800/' URLs. */
export function productImageVariantPath(input: string | null | undefined, size: ProductImageSize): string | null {
  if (![200, 400, 800].includes(size)) return null;
  const path = typeof input === 'string' ? input.trim() : '';
  if (!path) return null;
  // Historical batch uploads use processed/product-images/<name>.png. That is
  // NOT a versioned group and must continue using its original URL.
  if (!path.startsWith('product-images/') && !/(?:^|\/)product-images\/v\d+\//.test(path)) return path;
  const match = /^(.*\/)?product-images\/v1\/([a-f0-9]{32})\/800\/([^/?#]+)$/.exec(path);
  if (!match || !namePattern.test(match[3]) || /(?:^|\/)\.{1,2}(?:\/|$)/.test(path)) return null;
  return `${match[1] || ''}product-images/v1/${match[2]}/${size}/${match[3]}`;
}

/** Fixed purpose selection: no srcset, DPR switching, or fallback to the large member. */
export function getProductImageUrl(input: string | null | undefined, size: ProductImageSize): string | null {
  const path = productImageVariantPath(input, size);
  return path ? getImageUrl(path) : input ? placeholder : null;
}

/** Resolve both purposes from the original DB path; never prefix an already resolved URL twice. */
export function buildProductImageGallery(main: string | null | undefined, rounds: string | null | undefined) {
  const gallery: { large: string; thumbnail: string }[] = [];
  for (const original of [main, ...(rounds || '').split(',')]) {
    const large = getProductImageUrl(original, 800);
    const thumbnail = getProductImageUrl(original, 200);
    if (large && thumbnail && !gallery.some(image => image.large === large)) gallery.push({ large, thumbnail });
  }
  return gallery;
}

export function handleProductImageError(event: Event) {
  const image = event.target as HTMLImageElement | null;
  // A component may reuse this DOM node for another product. Only suppress an
  // error from the placeholder itself, not every subsequent image on the node.
  if (!image || image.getAttribute('src') === placeholder) return;
  image.dataset.productImageError = 'true';
  image.src = placeholder;
}
